import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Printer, ArrowLeft, Clock, CheckCircle, Stack, Users } from "@phosphor-icons/react";

export default function AssemblyRunView() {
  const { id } = useParams();
  const [run, setRun] = useState(null);
  const [settings, setSettings] = useState({ company_name: "US Operations" });
  const [boxes, setBoxes] = useState([]);
  const [contrib, setContrib] = useState(null);

  useEffect(() => {
    (async () => {
      const [r, s, ball, c] = await Promise.all([
        api.get(`/assembly-runs/${id}`), api.get("/settings"), api.get("/boxes"),
        api.get(`/assembly-runs/${id}/contributions`).catch(() => ({ data: null })),
      ]);
      setRun(r.data);
      setSettings(s.data);
      setBoxes(ball.data.filter((b) => (r.data.box_ids || []).includes(b.id)));
      setContrib(c.data);
    })();
  }, [id]);

  if (!run) return <div className="font-display uppercase">Loading...</div>;
  const status = run.status || "completed";
  const durMin = run.duration_seconds ? Math.round(run.duration_seconds / 60) : null;
  const totalRejected = (run.qa_rejections || []).reduce((a, b) => a + (b.qty_rejected || 0), 0);

  return (
    <div className="max-w-5xl mx-auto space-y-4" data-testid="run-view-page">
      <div className="flex items-center justify-between no-print">
        <Link to="/assembly-runs" className="brut-tag brut-tag-white inline-flex items-center gap-1">
          <ArrowLeft size={10} weight="bold" /> Back
        </Link>
        <div className="flex gap-2">
          {boxes.length > 0 && (
            <Link
              to={`/assembly-runs/${id}/labels`}
              className="brut-btn px-4 py-2 inline-flex items-center gap-2"
              data-testid="bulk-labels-btn"
            >
              <Stack size={14} weight="bold" /> Print {boxes.length} Label{boxes.length === 1 ? "" : "s"}
            </Link>
          )}
          <button onClick={() => window.print()} data-testid="print-run-btn" className="brut-btn brut-btn-primary px-4 py-2 inline-flex items-center gap-2">
            <Printer size={14} weight="bold" /> Print Run Slip
          </button>
        </div>
      </div>

      <div className="brut-card p-8 print-section" data-testid="run-document">
        <div className="flex justify-between items-start border-b-2 border-[#334155] pb-4">
          <div className="flex items-start gap-3">
            <img src="/logo-gate.jpg" alt="GATE" className="w-16 h-16 brut-border object-cover shrink-0" />
            <div>
              <div className="brut-tag brut-tag-yellow mb-1">Assembly Run Slip</div>
              <div className="font-display text-3xl uppercase tracking-tighter">{settings.company_name}</div>
            </div>
          </div>
          <div className="text-right">
            <div className="brut-label">Run Number</div>
            <div className="font-display text-2xl">{run.grn_number}</div>
            <div className="mt-1">
              {status === "in_progress"
                ? <span className="brut-tag brut-tag-yellow inline-flex items-center gap-1"><Clock size={10} weight="bold" /> In Progress</span>
                : <span className="brut-tag brut-tag-green inline-flex items-center gap-1"><CheckCircle size={10} weight="bold" /> Completed</span>}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4 mt-5">
          <div className="brut-border p-3 col-span-2">
            <div className="brut-label">Product</div>
            <div className="font-mono-ui text-sm font-bold">{run.product_part_number}</div>
            <div className="font-display text-xl uppercase">{run.product_name}</div>
            <div className="grid grid-cols-2 gap-3 mt-3 text-xs">
              <div>
                <div className="brut-label">Type</div>
                <div className="font-bold">{run.is_shippable_product ? "Shippable" : "Sub-Assembly"}</div>
              </div>
              <div>
                <div className="brut-label">Target Project</div>
                <div className="font-bold">{run.target_project || "Stock"}</div>
              </div>
              <div>
                <div className="brut-label">Target Units</div>
                <div className="font-display text-xl">{run.target_units}</div>
              </div>
              <div>
                <div className="brut-label">Boxes</div>
                <div className="font-display text-xl">{run.is_shippable_product ? (run.box_ids || []).length : "—"}</div>
              </div>
            </div>
          </div>
          <div className="brut-border p-3">
            <div className="brut-label">Timing</div>
            <div className="text-xs mt-1">Started:<br /><span className="font-mono-ui">{run.started_at ? new Date(run.started_at).toLocaleString() : "—"}</span></div>
            <div className="text-xs mt-2">Completed:<br /><span className="font-mono-ui">{run.completed_at ? new Date(run.completed_at).toLocaleString() : "—"}</span></div>
            <div className="text-xs mt-2">Duration:<br /><span className="font-display text-2xl">{durMin != null ? `${durMin} min` : "—"}</span></div>
          </div>
        </div>

        <div className="mt-5">
          <div className="brut-label mb-2">Components Consumed (Allocated)</div>
          <table className="brut-table">
            <thead><tr><th>#</th><th>Part #</th><th>Name</th><th>Qty</th><th>Batch (GRN)</th><th>Supplier</th></tr></thead>
            <tbody>
              {(run.components_consumed || []).map((c, i) => (
                <tr key={i}>
                  <td>{i + 1}</td>
                  <td className="font-bold">{c.part_number}</td>
                  <td>{c.part_name}</td>
                  <td className="font-mono-ui">{c.qty} {c.unit}</td>
                  <td className="font-mono-ui">{c.grn_number}</td>
                  <td>{c.supplier_name}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {(run.qa_rejections || []).length > 0 && (
          <div className="mt-5">
            <div className="brut-label mb-2">QA Rejections (Extra Stock Consumed) — Total: {totalRejected}</div>
            <table className="brut-table">
              <thead><tr><th>#</th><th>Part #</th><th>Qty Rejected</th><th>Replaced from Batch</th><th>Supplier</th><th>Reason</th></tr></thead>
              <tbody>
                {run.qa_rejections.map((r, i) => (
                  <tr key={i}>
                    <td>{i + 1}</td>
                    <td className="font-bold">{r.part_number}</td>
                    <td className="font-mono-ui">{r.qty_rejected} {r.unit}</td>
                    <td className="font-mono-ui">{r.grn_number}</td>
                    <td>{r.supplier_name}</td>
                    <td className="text-xs">{r.reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {boxes.length > 0 && (
          <div className="mt-5">
            <div className="brut-label mb-2">Boxes Generated</div>
            <table className="brut-table">
              <thead><tr><th>#</th><th>Box #</th><th>Units</th><th>Packing List</th><th></th></tr></thead>
              <tbody>
                {boxes.map((b, i) => (
                  <tr key={b.id}>
                    <td>{i + 1}</td>
                    <td className="font-bold">{b.box_number}</td>
                    <td className="font-mono-ui">{b.units_count}</td>
                    <td>{b.packing_list_number || <span className="text-zinc-500">—</span>}</td>
                    <td className="no-print">
                      <Link to={`/boxes/${b.id}/label`} className="brut-tag brut-tag-yellow">Label</Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {run.remarks && (
          <div className="mt-5 brut-border p-3"><div className="brut-label">Remarks</div><div className="text-sm">{run.remarks}</div></div>
        )}
        {run.completion_notes && (
          <div className="mt-3 brut-border p-3"><div className="brut-label">Completion Notes</div><div className="text-sm">{run.completion_notes}</div></div>
        )}

        {contrib && contrib.rows && (
          <div className="mt-5 no-print" data-testid="contrib-section">
            <div className="flex items-center justify-between mb-2">
              <div className="brut-label inline-flex items-center gap-2"><Users size={12} weight="bold" /> Workers & Contributions</div>
              <div className="text-xs font-mono-ui">
                {contrib.rows.length === 0
                  ? <span className="text-zinc-500">No linked operations yet</span>
                  : <span><b>{contrib.cumulative}</b> / {contrib.target_qty || "?"} pcs attributed</span>}
              </div>
            </div>
            {contrib.rows.length === 0 ? (
              <div className="brut-border p-3 text-xs text-zinc-600">
                No workers attributed yet. Link an Operation to this product&apos;s part on the <Link to="/operations" className="underline font-bold">Operations page</Link> so daily production auto-flows here.
              </div>
            ) : (
              <>
                {contrib.target_qty > 0 && (
                  <div className="brut-border p-1 mb-2 h-3 bg-slate-100">
                    <div className="h-full bg-blue-600" style={{ width: `${Math.min(100, (contrib.cumulative / contrib.target_qty) * 100)}%` }} />
                  </div>
                )}
                <table className="brut-table" data-testid="contrib-table">
                  <thead><tr><th>Worker</th><th>Role</th><th>Operations</th><th>Sessions</th><th className="text-right">Pieces</th><th className="text-right">% of Run</th></tr></thead>
                  <tbody>
                    {contrib.rows.map((r) => (
                      <tr key={r.staff_id} data-testid={`contrib-${r.staff_name}`}>
                        <td className="font-bold">{r.staff_name}</td>
                        <td><span className="brut-tag brut-tag-yellow text-[10px]">{r.designation}</span></td>
                        <td className="font-mono-ui text-xs">{r.operations.join(", ")}</td>
                        <td className="font-mono-ui">{r.sessions.length}</td>
                        <td className="text-right font-mono-ui font-bold">{r.produced}</td>
                        <td className="text-right font-mono-ui">{contrib.target_qty > 0 ? Math.round((r.produced / contrib.target_qty) * 100) + "%" : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
          </div>
        )}

        <div className="mt-8 grid grid-cols-2 gap-6 text-xs">
          {["Operator", "QA / Supervisor"].map((s) => (
            <div key={s} className="border-t-2 border-[#334155] pt-2 text-center">
              <div className="brut-label">{s}</div>
              <div className="h-12" />
              <div className="text-zinc-500">Signature</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
